import React, { useState, useEffect } from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Chip,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  Button,
  ButtonGroup,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  List as ListIcon,
  BugReport as BugIcon,
  Inventory2 as ArtifactIcon,
  Style as StyleIcon,
} from "@mui/icons-material";
import {
  useTriviaQuestions,
  useDeleteQuestions,
  useDeactivateQuestions,
  useActivateQuestions,
} from "../hooks/useTrivia";
import { QUERY_PAGE_SIZE_OPTIONS } from "../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";
import AdminPage from "../components/admin/AdminPage";
import AdminSection from "../components/admin/AdminSection";
import TriviaEngineWorkspace from "../components/trivia/engine/TriviaEngineWorkspace";
import TriviaArtifactReview from "../components/trivia/engine/TriviaArtifactReview";
import TriviaShareCardLab from "../components/trivia/TriviaShareCardLab";
import "./AdminPanel.css";

const AdminTrivia = () => {
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Bulk operations state
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortModel, setSortModel] = useState([
    { field: "question", sort: "asc" },
  ]);
  const sortedField = sortModel[0]?.field;

  // Custom hooks
  const {
    questions: allQuestions,
    totalResults,
    error: questionsError,
    isLoading: questionsLoading,
    refetch: refetchQuestions,
  } = useTriviaQuestions({
    page: paginationModel.page + 1, // Convert to 1-based for API
    pageSize: paginationModel.pageSize,
  });
  const { deleteTriviaQuestions } = useDeleteQuestions();
  const { deactivateTriviaQuestions } = useDeactivateQuestions();
  const { activateTriviaQuestions } = useActivateQuestions();

  // Handle errors from hooks
  useEffect(() => {
    if (questionsError) {
      setError(questionsError);
    } else {
      setError(null);
    }
  }, [questionsError]);

  const handleViewQuestion = (question) => {
    setSelectedQuestion(question);
    setShowQuestionModal(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      const result = await deleteTriviaQuestions([questionId]);
      if (result.success) {
        setSuccess("Question deleted successfully!");
        // Manually refetch the questions list to ensure UI is updated
        await refetchQuestions();
      } else {
        setError(result.error);
      }
    }
  };

  // Bulk operations handlers
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedRows.length} question(s)?`;
    if (window.confirm(confirmMessage)) {
      const result = await deleteTriviaQuestions(selectedRows);
      if (result.success) {
        setSuccess(`Successfully deleted ${selectedRows.length} question(s)!`);
        setSelectedRows([]);
        await refetchQuestions();
      } else {
        setError(result.error);
      }
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedRows.length === 0) return;

    const confirmMessage = `Are you sure you want to deactivate ${selectedRows.length} question(s)?`;
    if (window.confirm(confirmMessage)) {
      const result = await deactivateTriviaQuestions(selectedRows);
      if (result.success) {
        setSuccess(
          `Successfully deactivated ${selectedRows.length} question(s)!`
        );
        setSelectedRows([]);
        await refetchQuestions();
      } else {
        setError(result.error);
      }
    }
  };

  const handleBulkActivate = async () => {
    if (selectedRows.length === 0) return;

    const confirmMessage = `Are you sure you want to activate ${selectedRows.length} question(s)?`;
    if (window.confirm(confirmMessage)) {
      const result = await activateTriviaQuestions(selectedRows);
      if (result.success) {
        setSuccess(
          `Successfully activated ${selectedRows.length} question(s)!`
        );
        setSelectedRows([]);
        await refetchQuestions();
      } else {
        setError(result.error);
      }
    }
  };

  const handleCloseQuestionModal = () => {
    setShowQuestionModal(false);
    setSelectedQuestion(null);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Filter questions based on search and filters
  const filteredQuestions =
    allQuestions?.filter((question) => {
      const matchesSearch =
        question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (
          question.correctAnswer.displayName ||
          (question.type === "true_false"
            ? question.correctAnswer
              ? "True"
              : "False"
            : "")
        )
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesDifficulty =
        !difficultyFilter || question.difficulty === difficultyFilter;
      const matchesStatus =
        !statusFilter ||
        (statusFilter === "active" && question.active) ||
        (statusFilter === "inactive" && !question.active);

      return matchesSearch && matchesDifficulty && matchesStatus;
    }) || [];

  // Prepare data for DataGrid
  const questionsData = filteredQuestions.map((question) => ({
    id: question._id,
    question: question.question,
    correctAnswer:
      question.type === "true_false"
        ? question.correctAnswer
          ? "True"
          : "False"
        : question.correctAnswer.displayName,
    difficulty: question.difficulty,
    difficultyConfig: question.metadata?.difficultyConfig?.name || "Unknown",
    status: question.active ? "Active" : "Inactive",
    category: question.category,
    actions: question._id, // For action buttons
  }));

  const columns = [
    {
      field: "question",
      headerName: "Question",
      flex: 2,
      minWidth: 300,
      headerClassName: sortedClassFor("question", sortedField),
      cellClassName: sortedClassFor("question", sortedField),
      sortingOrder: NAME_SORT_ORDER,
      renderCell: (params) => (
        <Box sx={{ wordBreak: "break-word" }}>{params.value}</Box>
      ),
    },
    {
      field: "correctAnswer",
      headerName: "Correct Answer",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "difficulty",
      headerName: "Difficulty",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "easy"
              ? "success"
              : params.value === "medium"
              ? "warning"
              : "error"
          }
          size="small"
        />
      ),
    },
    {
      field: "difficultyConfig",
      headerName: "Difficulty Config",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "Legendary Players"
              ? "primary"
              : params.value === "Balanced"
              ? "secondary"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "Active" ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => {
            const question = allQuestions.find((q) => q._id === params.id);
            if (question) handleViewQuestion(question);
          }}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteQuestion(params.id)}
          color="error"
        />,
      ],
    },
  ];

  const tabNavigation = (
    <Paper sx={{ mb: 2 }}>
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab icon={<BugIcon />} label="Engine simulator" iconPosition="start" />
        <Tab icon={<ArtifactIcon />} label="Artifacts" iconPosition="start" />
        <Tab icon={<ListIcon />} label="Legacy bank" iconPosition="start" />
        <Tab icon={<StyleIcon />} label="Share cards" iconPosition="start" />
      </Tabs>
    </Paper>
  );

  const renderTool = () => {
    if (currentTab === 0) return <TriviaEngineWorkspace />;
    if (currentTab === 1) return <TriviaArtifactReview />;
    if (currentTab === 3) return <TriviaShareCardLab />;
    return null;
  };

  return (
    <AdminPage
      title="Trivia"
      subtitle="Tune the engine, review artifacts, and manage the bank."
      error={currentTab === 2 ? error : null}
      onDismissError={() => setError(null)}
    >
      {currentTab === 2 && success && (
        <div className="alert alert-success" onClick={() => setSuccess(null)}>
          {success}
        </div>
      )}

      {tabNavigation}
      {renderTool()}
      {currentTab === 2 && (
      <AdminSection title={`Legacy bank · ${filteredQuestions.length}`}>

            {/* Search and Filters */}
            <Box
              sx={{
                mb: 2,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                label="Search Questions"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 200 }}
                placeholder="Search by question or answer..."
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={difficultyFilter}
                  label="Difficulty"
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Bulk Operations */}
            {selectedRows.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`${selectedRows.length} selected`}
                    color="primary"
                    variant="outlined"
                  />
                  <ButtonGroup variant="outlined" size="small">
                    <Button
                      onClick={handleBulkDelete}
                      color="error"
                      startIcon={<DeleteIcon />}
                    >
                      Delete Selected
                    </Button>
                    <Button onClick={handleBulkActivate} color="success">
                      Activate Selected
                    </Button>
                    <Button onClick={handleBulkDeactivate} color="warning">
                      Deactivate Selected
                    </Button>
                  </ButtonGroup>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedRows([])}
                  >
                    Clear Selection
                  </Button>
                </Box>
              </Box>
            )}

            <Box sx={{ width: "100%" }}>
              <DataGrid
                rows={questionsData}
                columns={columns}
                pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                rowCount={totalResults}
                checkboxSelection
                onRowSelectionModelChange={setSelectedRows}
                rowSelectionModel={selectedRows}
                disableRowSelectionOnClick
                disableColumnMenu
                loading={questionsLoading}
                sortingMode="client"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                rowHeight={52}
                sx={getResultsGridSx()}
              />
            </Box>
      </AdminSection>
      )}

        {/* Question Detail Modal */}
        {showQuestionModal && selectedQuestion && (
          <div className="modal-overlay" onClick={handleCloseQuestionModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Question Details</h3>
                <button
                  className="modal-close"
                  onClick={handleCloseQuestionModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="question-detail">
                  <h4>Question:</h4>
                  <p>{selectedQuestion.question}</p>

                  <h4>Options:</h4>
                  <div className="options-list">
                    {selectedQuestion.options.map((option, index) => (
                      <div
                        key={index}
                        className={`option ${
                          (
                            selectedQuestion.type === "true_false"
                              ? option.value === selectedQuestion.correctAnswer
                              : option.playerId ===
                                selectedQuestion.correctAnswer.playerId
                          )
                            ? "correct"
                            : ""
                        }`}
                      >
                        {option.displayName}
                        {(selectedQuestion.type === "true_false"
                          ? option.value === selectedQuestion.correctAnswer
                          : option.playerId ===
                            selectedQuestion.correctAnswer.playerId) && (
                          <span className="correct-indicator">✓ Correct</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <h4>Correct Answer:</h4>
                  <p>
                    {selectedQuestion.type === "true_false"
                      ? selectedQuestion.correctAnswer
                        ? "True"
                        : "False"
                      : selectedQuestion.correctAnswer.displayName}
                  </p>

                  <div className="question-meta">
                    <div className="meta-item">
                      <strong>Category:</strong> {selectedQuestion.category}
                    </div>
                    <div className="meta-item">
                      <strong>Difficulty:</strong>{" "}
                      <span
                        className={`difficulty ${selectedQuestion.difficulty}`}
                      >
                        {selectedQuestion.difficulty}
                      </span>
                    </div>
                    <div className="meta-item">
                      <strong>Difficulty Config:</strong>{" "}
                      <span className="difficulty-config">
                        {selectedQuestion.metadata?.difficultyConfig?.name ||
                          "Unknown"}
                      </span>
                    </div>
                    <div className="meta-item">
                      <strong>Status:</strong>{" "}
                      <span
                        className={`status ${
                          selectedQuestion.active ? "active" : "inactive"
                        }`}
                      >
                        {selectedQuestion.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    {selectedQuestion._id && (
                      <div className="meta-item">
                        <strong>ID:</strong>
                        <span
                          style={{
                            wordBreak: "break-all",
                            fontFamily: "monospace",
                          }}
                        >
                          {selectedQuestion._id}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </AdminPage>
  );
};

export default AdminTrivia;
